import React, { useState, useEffect, useRef } from 'react';
import { Package, Wrench, Trash2, Plus, Loader2, CheckSquare } from 'lucide-react';
import { supabase, db } from '../services/db';
import { ConfirmModal } from './ui/ConfirmModal';

interface OSChecklistItem {
  id: string;
  os_id: string;
  tipo: 'material' | 'ferramenta';
  nome: string;
  concluido: boolean;
  criado_em: string;
  empresa_id: string;
}

interface OSChecklistProps {
  osId: string;
  userRole: string;
}

export const OSChecklist: React.FC<OSChecklistProps> = ({ osId, userRole }) => {
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmStyle?: 'danger' | 'primary';
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  const [items, setItems] = useState<OSChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [empresaId, setEmpresaId] = useState<string | null>(null);

  const [newMaterial, setNewMaterial] = useState('');
  const [newTool, setNewTool] = useState('');

  const materialInputRef = useRef<HTMLInputElement>(null);
  const toolInputRef = useRef<HTMLInputElement>(null);

  const isEditable = userRole === 'ADMIN' || userRole === 'ASSISTANT';
  const canToggle = userRole === 'ADMIN' || userRole === 'ASSISTANT' || userRole === 'TECHNICIAN';

  useEffect(() => {
    const init = async () => {
      try {
        const company = await db.getCurrentCompany();
        if (company) {
          setEmpresaId(company.id);
        }
        await loadItems();
      } catch (err) {
        console.error('Erro na inicialização do checklist:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [osId]);

  const loadItems = async () => {
    const { data, error } = await supabase
      .from('os_checklist_items')
      .select('*')
      .eq('os_id', osId)
      .order('criado_em', { ascending: true });

    if (error) {
      console.error('Erro ao carregar itens do checklist:', error);
    } else if (data) {
      setItems(data);
    }
  };

  const handleAddItem = async (tipo: 'material' | 'ferramenta', nome: string) => {
    const trimmedNome = nome.trim();
    if (!trimmedNome || !empresaId) return;

    const { data, error } = await supabase
      .from('os_checklist_items')
      .insert({
        os_id: osId,
        tipo,
        nome: trimmedNome,
        empresa_id: empresaId,
        concluido: false
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao adicionar item:', error);
      setConfirmModal({
        title: 'Erro ao Adicionar',
        message: 'Erro ao adicionar item: ' + error.message,
        onConfirm: () => setConfirmModal(null)
      });
    } else if (data) {
      setItems(prev => [...prev, data]);
      if (tipo === 'material') {
        setNewMaterial('');
        setTimeout(() => materialInputRef.current?.focus(), 50);
      } else {
        setNewTool('');
        setTimeout(() => toolInputRef.current?.focus(), 50);
      }
    }
  };

  const handleToggleCheck = async (itemId: string, currentStatus: boolean) => {
    if (!canToggle) return;

    // Optimistic update
    setItems(prev => prev.map(item => item.id === itemId ? { ...item, concluido: !currentStatus } : item));

    const { error } = await supabase
      .from('os_checklist_items')
      .update({ concluido: !currentStatus })
      .eq('id', itemId);

    if (error) {
      console.error('Erro ao atualizar item:', error);
      // Rollback
      setItems(prev => prev.map(item => item.id === itemId ? { ...item, concluido: currentStatus } : item));
      setConfirmModal({
        title: 'Erro ao Atualizar',
        message: 'Erro ao atualizar item: ' + error.message,
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!isEditable) return;

    const { error } = await supabase
      .from('os_checklist_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      console.error('Erro ao excluir item:', error);
      setConfirmModal({
        title: 'Erro ao Excluir',
        message: 'Erro ao excluir item: ' + error.message,
        onConfirm: () => setConfirmModal(null)
      });
    } else {
      setItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  const materials = items.filter(item => item.tipo === 'material');
  const tools = items.filter(item => item.tipo === 'ferramenta');

  const completedMaterials = materials.filter(m => m.concluido).length;
  const completedTools = tools.filter(t => t.concluido).length;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <Loader2 size={32} className="animate-spin text-brand-500 mb-2" />
        <span>Carregando checklist...</span>
      </div>
    );
  }

  return (
    <div 
      className="w-full flex flex-col space-y-6"
      style={{ 
        '--sf-blue': 'var(--blue)', 
        '--bg2': 'rgba(255,255,255,.03)', 
        '--bg3': 'rgba(255,255,255,.02)', 
        '--border': 'var(--border)', 
        '--muted': 'var(--muted)' 
      } as React.CSSProperties}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Bloco Materiais */}
        <div className="p-5 rounded-xl border" style={{ background: 'var(--bg2)', borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2">
              <Package size={20} className="text-brand-500" />
              <h3 className="font-bold text-gray-900 text-base">Materiais</h3>
            </div>
            <span className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>
              ({completedMaterials} / {materials.length} concluídos)
            </span>
          </div>

          {isEditable && (
            <form 
              onSubmit={(e) => { e.preventDefault(); handleAddItem('material', newMaterial); }}
              className="flex gap-2 mb-4"
            >
              <input
                ref={materialInputRef}
                type="text"
                value={newMaterial}
                onChange={(e) => setNewMaterial(e.target.value)}
                placeholder="Adicionar novo material..."
                className="flex-1 p-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-brand-500 text-gray-900 bg-white"
                style={{ borderColor: 'var(--border)' }}
              />
              <button
                type="submit"
                className="text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-1.5 transition-colors"
                style={{ background: 'var(--sf-blue)' }}
              >
                <Plus size={16} /> Adicionar
              </button>
            </form>
          )}

          {materials.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: 'var(--muted)' }}>
              Nenhum material adicionado
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {materials.map(item => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between p-2.5 rounded border"
                  style={{ background: 'var(--bg3)', borderColor: 'var(--border)' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      type="button"
                      disabled={!canToggle}
                      onClick={() => handleToggleCheck(item.id, item.concluido)}
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                        !canToggle ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                      } ${
                        item.concluido 
                          ? 'bg-brand-600 border-brand-600 text-white' 
                          : 'bg-white border-gray-400 hover:border-brand-500'
                      }`}
                    >
                      {item.concluido && <CheckSquare size={13} className="text-white" />}
                    </button>
                    <span 
                      className={`text-sm font-medium truncate ${
                        item.concluido ? 'line-through' : ''
                      }`}
                      style={{ color: item.concluido ? 'var(--muted)' : 'var(--sf-white)' }}
                    >
                      {item.nome}
                    </span>
                  </div>
                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(item.id)}
                      className="text-rose-400 hover:text-rose-600 p-1 flex-shrink-0 transition-colors"
                      title="Excluir item"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bloco Ferramentas */}
        <div className="p-5 rounded-xl border" style={{ background: 'var(--bg2)', borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2">
              <Wrench size={20} className="text-brand-500" />
              <h3 className="font-bold text-gray-900 text-base">Ferramentas</h3>
            </div>
            <span className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>
              ({completedTools} / {tools.length} concluídos)
            </span>
          </div>

          {isEditable && (
            <form 
              onSubmit={(e) => { e.preventDefault(); handleAddItem('ferramenta', newTool); }}
              className="flex gap-2 mb-4"
            >
              <input
                ref={toolInputRef}
                type="text"
                value={newTool}
                onChange={(e) => setNewTool(e.target.value)}
                placeholder="Adicionar nova ferramenta..."
                className="flex-1 p-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-brand-500 text-gray-900 bg-white"
                style={{ borderColor: 'var(--border)' }}
              />
              <button
                type="submit"
                className="text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-1.5 transition-colors"
                style={{ background: 'var(--sf-blue)' }}
              >
                <Plus size={16} /> Adicionar
              </button>
            </form>
          )}

          {tools.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: 'var(--muted)' }}>
              Nenhuma ferramenta adicionada
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {tools.map(item => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between p-2.5 rounded border"
                  style={{ background: 'var(--bg3)', borderColor: 'var(--border)' }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      type="button"
                      disabled={!canToggle}
                      onClick={() => handleToggleCheck(item.id, item.concluido)}
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                        !canToggle ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                      } ${
                        item.concluido 
                          ? 'bg-brand-600 border-brand-600 text-white' 
                          : 'bg-white border-gray-400 hover:border-brand-500'
                      }`}
                    >
                      {item.concluido && <CheckSquare size={13} className="text-white" />}
                    </button>
                    <span 
                      className={`text-sm font-medium truncate ${
                        item.concluido ? 'line-through' : ''
                      }`}
                      style={{ color: item.concluido ? 'var(--muted)' : 'var(--sf-white)' }}
                    >
                      {item.nome}
                    </span>
                  </div>
                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(item.id)}
                      className="text-rose-400 hover:text-rose-600 p-1 flex-shrink-0 transition-colors"
                      title="Excluir item"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
      {confirmModal && (
        <ConfirmModal
          open={!!confirmModal}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          confirmStyle={confirmModal.confirmStyle}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </div>
  );
};
